<template>
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
    <div class="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <!-- 背景遮罩 -->
      <div class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" aria-hidden="true" @click="$emit('close')"></div>
      
      <!-- 模态框居中对齐 -->
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
      
      <!-- 模态框内容 -->
      <div class="inline-block overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full w-[90vw] max-w-[90vw] mx-auto">
        <!-- 模态框头部 -->
        <div class="px-6 py-4 border-b border-gray-200">
          <h3 class="text-lg font-semibold text-gray-900" id="modal-title">
            批量添加标签
          </h3>
          <p class="text-sm text-gray-600 mt-1">为选中的 {{ selectedCount }} 个客户批量添加标签</p>
        </div>
        
        <!-- 模态框主体 -->
        <div class="px-6 py-4">
          <div v-if="tags.length === 0" class="text-center py-8 text-gray-500">
            <TagIcon class="mx-auto h-12 w-12 text-gray-400" />
            <p class="mt-2">暂无标签</p>
            <p class="text-sm">请先在标签管理中创建标签</p>
          </div>
          
          <div v-else>
            <div class="flex items-center justify-between mb-3">
              <label class="text-sm font-medium text-gray-700">选择要添加的标签：</label>
              <input
                v-model="searchQuery"
                type="text"
                placeholder="搜索标签..."
                class="w-32 h-7 px-2 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div class="max-h-60 overflow-y-auto">
              <div v-if="filteredTags.length > 0" class="flex flex-wrap gap-2">
                <label v-for="tag in filteredTags" :key="tag.id" class="cursor-pointer">
                  <input 
                    type="checkbox" 
                    :value="tag.id"
                    v-model="selectedTagIds"
                    class="sr-only" 
                  />
                  <span 
                    class="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full transition-colors"
                    :class="selectedTagIds.includes(tag.id) 
                      ? 'bg-blue-100 text-blue-800 border border-blue-300' 
                      : 'bg-gray-100 text-gray-800 border border-gray-300 hover:bg-gray-200'"
                    :style="selectedTagIds.includes(tag.id) 
                      ? { backgroundColor: tag.background_color || '#DBEAFE', color: tag.text_color || '#1E40AF' } 
                      : {}"
                  >
                    {{ tag.name }}
                    <span class="ml-1 text-gray-500">({{ tag.customer_count || 0 }})</span>
                  </span>
                </label>
              </div>
              <div v-else class="text-center py-4 text-sm text-gray-400">
                未找到匹配的标签
              </div>
            </div>
            
            <div v-if="selectedTagIds.length > 0" class="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p class="text-sm text-blue-700">
                将为 <span class="font-medium">{{ selectedCount }}</span> 个客户添加 
                <span class="font-medium">{{ selectedTagIds.length }}</span> 个标签
              </p>
            </div>
          </div>
        </div>
        
        <!-- 模态框底部 -->
        <div class="px-6 py-4 bg-gray-50 flex justify-end space-x-3">
          <button
            @click="$emit('close')"
            type="button"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors"
          >
            取消
          </button>
          <button
            @click="handleConfirm"
            :disabled="selectedTagIds.length === 0 || isProcessing"
            type="button"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <svg v-if="isProcessing" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ isProcessing ? '处理中...' : `添加标签 (${selectedTagIds.length})` }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { TagIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  tags: {
    type: Array,
    default: () => []
  },
  selectedCount: {
    type: Number,
    default: 0
  }
})

const emit = defineEmits(['close', 'confirm'])

const selectedTagIds = ref([])
const isProcessing = ref(false)
const searchQuery = ref('')

// 过滤标签
const filteredTags = computed(() => {
  if (!searchQuery.value.trim()) {
    return props.tags
  }
  const query = searchQuery.value.toLowerCase()
  return props.tags.filter(tag => 
    tag.name.toLowerCase().includes(query)
  )
})

// 重置选择状态
watch(() => props.show, (newShow) => {
  if (!newShow) {
    selectedTagIds.value = []
    isProcessing.value = false
    searchQuery.value = ''
  }
})

const handleConfirm = async () => {
  if (selectedTagIds.value.length === 0) {
    return
  }
  
  isProcessing.value = true
  try {
    await emit('confirm', selectedTagIds.value)
    emit('close')
  } catch (error) {
    console.error('批量添加标签失败:', error)
  } finally {
    isProcessing.value = false
  }
}
</script>
